import { cn } from '@/lib/utils'
import { eventDotStyle } from '@/types/equipment'
import type { EventTypeRow } from '@/hooks/useEventTypes'

/**
 * Color-coded dot + label for each event type in the catalog. When `onToggle` is
 * provided the legend becomes an interactive filter — clicking a type toggles it
 * (keyed on the type's `code`), and inactive types render dimmed.
 */
export function EquipmentLegend({
  className,
  eventTypes,
  activeCodes,
  onToggle,
}: {
  className?: string
  eventTypes: EventTypeRow[]
  activeCodes?: string[]
  onToggle?: (code: string) => void
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-1.5', className)}>
      {eventTypes.map((type) => {
        const active = !activeCodes || activeCodes.includes(type.code)
        const content = (
          <>
            <span
              className='h-2 w-2 shrink-0 rounded-full'
              style={active ? eventDotStyle(type.color) : { backgroundColor: '#d1d5db' }}
            />
            {type.label}
          </>
        )
        if (!onToggle) {
          return (
            <span
              key={type.code}
              className='inline-flex items-center gap-1.5 px-1.5 text-xs text-gray-600'
            >
              {content}
            </span>
          )
        }
        return (
          <button
            key={type.code}
            type='button'
            onClick={() => onToggle(type.code)}
            title={active ? `Hide ${type.label}` : `Show ${type.label}`}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs transition-colors',
              active ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-100',
            )}
          >
            {content}
          </button>
        )
      })}
    </div>
  )
}
